//imports
import LanguageRepository from '../Database/Repositories/LanguageRepository';
import TranslationRepository from '../Database/Repositories/TranslationRepository';
import Config from '../Config.json';
import APIAction from './APIAction';
import UserAction from './UserAction';
import LanguageAction from './LanguageAction';

//localStorage key shared by every screen's translate-button "seen it once" hint balloon
const TRANSLATION_HINT_KEY = 'hasSeenTranslationHint';

//define class
let TranslationAction = {

    //init
    init: function() {
        //language respository
        this.languageRepository = new LanguageRepository();
        this.translationRepository = new TranslationRepository();
    },

    //get languages
    languages: async function() {
        return this.languageRepository.getAll();
    },

    //get current
    currentLanguage: async function() {
        return this.languageRepository.getSelected();
    },

    //translate
    translate: async function(key) {
        //get active language
        let language = await this.languageRepository.getSelected();

        //if null return key
        if (language == null) {
            return key.replace('KEY.', '');
        }

        //search for translation
        let translation = await this.translationRepository.get(key, language);

        //if null return key
        if (translation == null) {
            return key.replace('KEY.', '');
        }

        //return
        return translation.translation;
    },

    //set current language
    setLanguage: async function(code, fallbackName = null) {
        //get language
        let language = await this.languageRepository.get(code);

        //language not in local DB yet (e.g. newly enabled in backoffice)
        if (!language) {
            await this.updateLanguages(true);
            language = await this.languageRepository.get(code);
        }

        //seed from API data if translation URL doesn't carry this language
        if (!language && fallbackName) {
            await this.languageRepository.save({name: fallbackName, code: code, selected: 0, createdAt: new Date()}, code);
            language = await this.languageRepository.get(code);
        }

        if (language) {
            await this.languageRepository.setSelected(language);
        } else {
        }
    },

    //update
    update: async function(force = false) {
        let langUpdated = await this.updateLanguages(force);
        let transUpdated = await this.updateTranslations(force);
        return langUpdated || transUpdated;
    },

    //refresh the local language cache from the live UI-languages endpoint
    updateLanguages: async function(force = false) {
        let langUpdate = await this.languageRepository.updateRequired();
        if (!force && !langUpdate) {
            return false;
        }

        let languages = await LanguageAction.getAllUILanguages();
        if (!Array.isArray(languages)) {
            return false;
        }

        await this.languageRepository.import(languages);
        return true;
    },

    //refresh the local translation-strings cache from the static translations.json
    updateTranslations: async function(force = false) {
        let transUpdate = await this.translationRepository.updateRequired();
        if (!force && !transUpdate) {
            return false;
        }

        //request translation
        let translationsImport = undefined;
        try {
            translationsImport = await fetch(Config.TRANSLATION_URL)
                .then(
                    response => response.json()
                ).catch(error => {
                    return undefined;
                });
        } catch (e) {
            translationsImport = undefined;
        }

        //check if ok
        if (typeof translationsImport === 'undefined') {
            return false;
        }

        //import translation
        await this.translationRepository.import(translationsImport.translations);
        return true;
    },

    //translate based on array from request
    translateArray: async function(input) {

        //check if is object
        if ((input === null) || (typeof input !== 'object')) {
            return '';
        }

        //change keys to lower case
        let temp = {};
        //loop
        Object.keys(input).forEach(function(key) {
            temp[key.toLowerCase()] = input[key];
        });
        //set
        input = temp;

        //get current language
        let language = await this.languageRepository.getSelected();

        //check if language is supported
        if (
            (language !== null) &&
            (language.code in input)
        ) {
            return input[language.code];
        }

        //use config value as fallback
        if (Config.LOCALE in input) {
            return input[Config.LOCALE];
        }

        //use first key as last resort
        return input[Object.keys(input)[0]];
    },

    translateBulkDeepL: async function(fields = [], targetLangId = '', sourceLangId = null) {
        let translations = await APIAction.request('/translate/text', 'POST', true, {texts: fields, target_language_id: targetLangId, source_language_id: sourceLangId})

        return translations;
    },

    //initial state shape shared by every screen with an on-the-fly translate button
    getInitialTranslationState: function () {
        return {
            showTranslation: false,
            loadingTranslations: false,
            selectedLanguage: null,
            availableLanguages: [],
            showTranslationHint: false,
        };
    },

    //has the user already dismissed the translate-button hint balloon (on any screen)?
    hasSeenTranslationHint: function () {
        return !!localStorage.getItem(TRANSLATION_HINT_KEY);
    },

    //remember that the hint balloon was dismissed, so it doesn't show again on any screen
    markTranslationHintSeen: function () {
        localStorage.setItem(TRANSLATION_HINT_KEY, JSON.stringify(true));
    },

    //lazily load the allowed DeepL languages and resolve the default selection.
    //returns null when `existingAvailableLanguages` is already populated, so callers can do `... ?? {}`
    //and skip re-fetching. `fallbackToFirst` picks availableLanguages[0] when no saved default matches.
    loadAvailableLanguages: async function (currentUser, existingAvailableLanguages = [], currentSelectedLanguage = null, fallbackToFirst = false) {
        if (existingAvailableLanguages.length > 0) {
            return null;
        }

        const targetLang = currentUser?.motherLanguage?.languageCode ?? currentUser?.defaultCompany?.defaultLanguage?.languageCode ?? 'en';
        const rawLanguages = await LanguageAction.getAllowedDeepLLanguages();
        const availableLanguages = rawLanguages.map(l => ({
            label: l.nativeName ?? (typeof l.name === 'object' ? (l.name[targetLang] ?? Object.values(l.name)[0]) : l.name) ?? l.languageCode,
            value: l.id,
        }));

        const defaultLangId = currentUser?.motherLanguage?.id ?? currentUser?.defaultCompany?.defaultLanguage?.id ?? null;
        let defaultLanguage = availableLanguages.find(l => l.value === defaultLangId) ?? null;
        if (!defaultLanguage && fallbackToFirst) {
            defaultLanguage = availableLanguages[0] ?? null;
        }

        return {
            availableLanguages,
            selectedLanguage: currentSelectedLanguage ?? defaultLanguage,
        };
    },

    //resolve the user's saved DeepL language preference, if any, so the translate button can skip asking
    resolveSavedLanguage: function (currentUser, availableLanguages) {
        return currentUser?.motherLanguage
            ? (availableLanguages ?? []).find(l => l.value === currentUser.motherLanguage.id) ?? null
            : null;
    },

    //read the active translation (language + on/off) for a given session key (e.g. an LMRA/ICL id),
    //so navigating between its list and item screens (a hard page reload, not a SPA transition) shows
    //the same translation without requiring the permanent "remember choice" profile preference.
    //Backed by sessionStorage - survives a reload within the tab, gone once the tab closes.
    getTranslationSession: function (sessionKey) {
        try {
            const raw = sessionStorage.getItem(`translation_session_${sessionKey}`);
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    },

    //remember the active translation for a session key
    setTranslationSession: function (sessionKey, session) {
        try {
            sessionStorage.setItem(`translation_session_${sessionKey}`, JSON.stringify(session));
        } catch (e) {
            //ignore (e.g. sessionStorage unavailable/full)
        }
    },

    //persist the user's "remember choice" language preference; returns the patch to merge into local user state
    rememberLanguage: async function (userId, language) {
        await UserAction.editUser(userId, { motherLanguage: `/api/deep_l_languages/${language.value}` });
        return { motherLanguage: { id: language.value } };
    },

    //translate a plain list of strings, returning the translations in the same order
    translateTextList: async function (texts, targetLangId) {
        if (!texts || texts.length === 0) {
            return [];
        }

        const response = await this.translateBulkDeepL(texts, targetLangId);
        return response.translations;
    },

    //translate the non-empty fields of `source` (optionally including one level of dot-notation
    //nested fields, and/or extra standalone strings) in a single batched DeepL call, remapping the
    //results back onto a shallow copy of `source`.
    translateFlatFields: async function (source, flatFields, targetLangId, { nestedFields = [], extraTexts = [] } = {}) {
        const fieldsToTranslate = [];
        const valuesToTranslate = [];

        flatFields.forEach(field => {
            if (source[field]) {
                fieldsToTranslate.push(field);
                valuesToTranslate.push(source[field]);
            }
        });

        nestedFields.forEach(field => {
            const [parent, child] = field.split('.');
            if (source[parent] && source[parent][child]) {
                fieldsToTranslate.push(field);
                valuesToTranslate.push(source[parent][child]);
            }
        });

        const allTexts = [...valuesToTranslate, ...extraTexts];
        if (allTexts.length === 0) {
            return { translated: { ...source }, extraTranslations: extraTexts.map(() => undefined) };
        }

        const translations = await this.translateTextList(allTexts, targetLangId);

        const translated = { ...source };
        fieldsToTranslate.forEach((field, index) => {
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                translated[parent] = { ...translated[parent], [child]: translations[index] };
            } else {
                translated[field] = translations[index];
            }
        });

        const extraTranslations = extraTexts.map((_, index) => translations[valuesToTranslate.length + index]);

        return { translated, extraTranslations };
    }

};

//export
export default TranslationAction;